import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Accordion, SelectDataInTable } from '~/components';
import { DateTime, Input, Checkbox } from '~/components/Form';
import { PesquisaTabela } from '~/components/Form/v2';
import { number, t } from '~/helpers';
import { validationErrors } from '~/helpers/yupValidations';
import { closeDialog } from '~/store/modules/dialog/actions';
import { updateOperacaoAbastecimentoExterno } from '~/store/modules/operacaoabastecimentoexterno/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleConfirmButton,
}) {
  const [motorista, setMotorista] = useState([]);
  const [veiculo, setVeiculo] = useState([]);
  const [posto, setPosto] = useState([]);

  const dispatch = useDispatch();

  const { loading, find, params } = useSelector(
    (state) => state.operacaoabastecimentoexterno
  );

  const initials = create ? data : { ...find, ...data };

  const cellsCompany = useMemo(
    () => [
      {
        id: 'cnpjposto',
        label: t('s.cadastro.empresaveiculo.placaveiculo'),
        filter: { type: 'text' },
        formatted: (value) =>
          value.replace(
            /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            '$1.$2.$3/$4-$5'
          ),
      },
      {
        id: 'nomeposto',
        label: t('s.cadastro.empresaveiculo.nomemodeloveiculo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const cellsVeiculo = useMemo(
    () => [
      {
        id: 'idveiculo',
        label: t('s.cadastro.veiculo.idveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.cadastro.veiculo.idmodeloveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'placaveiculo',
        label: t('s.cadastro.veiculo.placaveiculo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const cellsDriver = useMemo(
    () => [
      {
        id: 'idmotorista',
        label: t('s.cadastro.motorista.idmotorista'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemotorista',
        label: t('s.cadastro.motorista.nomemotorista'),
        filter: { type: 'text' },
      },
      {
        id: 'codigoauxiliarmotorista',
        label: t('s.cadastro.modeloveiculo.codigoauxiliarmodeloveiculo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  const fieldsCombustiveis = useMemo(
    () => [
      {
        id: 'idabastprodutonf',
        label: 'Produto',
      },
      {
        id: 'idbico',
        label: 'Bico',
      },
      {
        id: 'idmovimentacaolubri',
        label: 'Lubrificacao',
      },

      {
        id: 'precototalabastnf',
        label: 'Preço Total',
      },
      {
        id: 'precounitarioabastnf',
        label: 'Preço Unitário',
      },
      {
        id: 'volumenfabastnf',
        label: 'Volume Total',
      },
    ],
    []
  );

  const cellsCombustiveis = useMemo(
    () => [
      {
        id: 'nomeproduto',
        label: 'Produto',
        // format: (value) =>
        //   produtos.find((item) => item.idproduto === value)
        //     ?.nomeabreviadoproduto ?? '--',
      },
      {
        id: 'idbico',
        label: 'Bico',
      },
      {
        id: 'precototalabastnf',
        label: 'Preço Total',
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'precounitarioabastnf',
        label: 'Preço Unitário',
        align: 'right',
        formatted: (value) => number(value, 4),
      },
      {
        id: 'volumenfabastnf',
        label: 'Volume Total',
        align: 'right',
        formatted: (value) => number(value, 3),
      },
    ],
    []
  );

  const cellsLubrificantes = useMemo(
    () => [
      {
        id: 'nomeproduto',
        label: 'Produto',
        // format: (value) =>
        //   produtos.find((item) => item.idproduto === value)
        //     ?.nomeabreviadoproduto ?? '--',
      },
      {
        id: 'idbico',
        label: 'Bico',
      },
      {
        id: 'idcompartimento',
        label: 'Compartimento',
      },

      {
        id: 'precototalabastnf',
        label: 'Preço Total',
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'precounitarioabastnf',
        label: 'Preço Unitário',
        align: 'right',
        formatted: (value) => number(value, 4),
      },
      {
        id: 'volumenfabastnf',
        label: 'Volume Total',
        align: 'right',
        formatted: (value) => number(value, 3),
      },
    ],
    []
  );

  const accordions = useMemo(
    () => [
      {
        id: 'combustiveis',
        title: t('Combustíveis'),
        component: () => (
          <SelectDataInTable
            icons={false}
            checkbox={false}
            showPagination={false}
            paginate={99999}
            create={false}
            noRemove
            size="small"
            variant="contained"
            position="row"
            onChanged={() => {}}
            unique="idproduto"
            maxWidth="xs"
            initials={find.combustiveis}
            fields={fieldsCombustiveis}
            schema={() => {}}
            cells={cellsCombustiveis}
            titleModal={t('Combustíveis')}
          />
        ),
      },
      {
        id: 'lubrificantes',
        title: t('Lubrificantes'),
        component: () => (
          <SelectDataInTable
            icons={false}
            checkbox={false}
            showPagination={false}
            paginate={99999}
            create={false}
            noRemove
            size="small"
            variant="contained"
            position="row"
            onChanged={() => {}}
            unique="idproduto"
            maxWidth="xs"
            initials={find.lubrificantes}
            fields={fieldsCombustiveis}
            schema={() => {}}
            cells={cellsLubrificantes}
            titleModal={t('Lubrificantes')}
          />
        ),
      },
    ],
    [find]
  );

  async function handleSubmit(param) {
    try {
      const notaAtualizada = {
        ...initials,
        idposto: posto.idposto ?? initials.idposto,
        idveiculo: veiculo.idveiculo ?? initials.idveiculo,

        idempresaveiculo:
          veiculo.idveiculo === initials.idveiculo
            ? initials.idempresaveiculo
            : veiculo.idempresaveiculo ?? initials.idempresaveiculo,

        placactabastnf: veiculo.placaveiculo ?? initials.placactabastnf,
        motoristaabastnf: motorista.idmotorista ?? initials.motoristaabastnf,
        idmotorista: motorista.idmotorista ?? initials.idmotorista,
        totalizadorveiculo: param.totalizadorveiculo,
        liberalocalveiculo: param.liberalocalveiculo
          ? 'S'
          : initials.liberalocalveiculo,
        liberamotoristaveiculo: param.liberamotoristaveiculo
          ? 'S'
          : initials.liberamotoristaveiculo,
        liberakmveiculo: param.liberakmveiculo ? 'S' : initials.liberakmveiculo,
      };

      dispatch(updateOperacaoAbastecimentoExterno(notaAtualizada, params));
      dispatch(closeDialog());
    } catch (err) {
      validationErrors(err, reference);
    }
  }

  useEffect(() => {
    if (initials.statusabastnf === 1) {
      toggleConfirmButton(false);
    } else {
      toggleConfirmButton(true);
    }
  }, []);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Input disabled name="numeroabastnf" label={t('Número NF')} />
            <DateTime
              label={t('Data emissâo')}
              name="dataemissaoabastnf"
              disabled
            />
            {initials.nomepostonf && (
              <Input
                disabled
                name="nomepostonf"
                label={t('Nome do Posto NF')}
              />
            )}

            <PesquisaTabela
              name="cnpjdistribuidoraabastnf"
              label={t('Posto')}
              primary="cnpjposto"
              secondary="nomeposto"
              url="postos"
              header={cellsCompany}
              getValue={(value) => setPosto(value)}
              amaggi
              disabled
              filterByUser={false}
            />
            <PesquisaTabela
              name="idmotorista"
              label={t('s.menu.cadastro.motorista')}
              primary="idmotorista"
              secondary="nomemotorista"
              url="motoristas"
              header={cellsDriver}
              getValue={(value) => setMotorista(value)}
              amaggi
            />
            <PesquisaTabela
              name="placactabastnf"
              label={t('Veiculo')}
              primary="placaveiculo"
              secondary="nomemodeloveiculo"
              url="veiculos"
              header={cellsVeiculo}
              getValue={(value) => setVeiculo(value)}
              amaggi
            />
            <Input
              name="totalizadorveiculo"
              type="number"
              label={t('Totalizador veículo')}
            />

            <DateTime
              label={t('Data/Hora Entrada')}
              name="datahoraentradasaidaabastnf"
              time
              disabled
            />
            <Input name="chavenf" type="text" label={t('Chave NF')} disabled />
            <Input
              name="mensagemabastnf"
              type="text"
              label={t('Mensagem Status')}
              disabled
            />
            {find?.statusabastnf == 11 && (
              <Checkbox
                name="liberakmveiculo"
                label={t('Libera a verificação do odômetro no Autotrac')}
                switches
              />
            )}
            {find?.statusabastnf == 8 && (
              <Checkbox
                name="liberalocalveiculo"
                label={t(
                  'Libera a verificação da localização do veículo no Autotrac'
                )}
                switches
              />
            )}
            {find?.statusabastnf == 9 && (
              <Checkbox
                name="liberamotoristaveiculo"
                label={t(
                  'Libera a verificação do motorista presente no veículo no Autotrac'
                )}
                switches
              />
            )}
          </Form>

          <Accordion accordions={accordions} />
        </>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func.isRequired,
};

CreateOrUpdate.defaultProps = {
  data: {},
};
